import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api, formatCzk, formatDate, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, MessageSquare, Gavel, Wallet } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  open: { label: "Otevřený", color: "bg-amber-100 text-amber-800" },
  in_review: { label: "V řešení", color: "bg-sky-100 text-sky-800" },
  resolved_full_refund: { label: "Plná náhrada", color: "bg-emerald-100 text-emerald-800" },
  resolved_partial_refund: { label: "Částečná náhrada", color: "bg-indigo-100 text-indigo-800" },
  resolved_no_refund: { label: "Bez náhrady", color: "bg-slate-100 text-slate-700" },
};

const CATEGORY_LABEL = {
  damaged: "Poškozené prádlo",
  missing_item: "Chybějící kus",
  late_delivery: "Pozdní doručení",
  quality: "Nespokojenost s kvalitou",
  other: "Jiné",
};

const DisputeDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [dispute, setDispute] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // admin resolution form
  const [action, setAction] = useState("partial_refund");
  const [refundAmount, setRefundAmount] = useState(0);
  const [adminNote, setAdminNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [order, setOrder] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/disputes/${id}`);
      setDispute(data);
      if (user?.role === "admin" && data.order_id) {
        try {
          const o = await api.get(`/orders/${data.order_id}`);
          setOrder(o.data);
          if (!refundAmount) setRefundAmount(Math.round((o.data.total_price || 0) * 0.5));
        } catch (err) { console.error("Failed to load order for dispute:", err); }
      }
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => { load(); }, [id]);

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/disputes/${id}/message`, { message });
      setMessage("");
      await load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSending(false); }
  };

  const resolve = async () => {
    setResolving(true);
    try {
      await api.post(`/admin/disputes/${id}/resolve`, {
        action,
        refund_amount: action === "partial_refund" ? Number(refundAmount) : 0,
        admin_note: adminNote,
      });
      toast.success("Spor uzavřen");
      await load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setResolving(false); }
  };

  if (!dispute) return (<div><Navbar /><div className="container-page py-16 text-center text-slate-500">Načítání…</div></div>);

  const meta = STATUS_META[dispute.status] || STATUS_META.open;
  const resolved = dispute.status.startsWith("resolved");
  const backLink = user?.role === "admin" ? "/admin" :
                    user?.role === "provider" ? "/dashboard/provider" : "/dashboard/customer";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10 max-w-5xl">
        <Link to={backLink} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600">
          <ArrowLeft className="w-4 h-4" /> Zpět
        </Link>

        <div className="mt-4 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-7">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="tiny-label mb-2">Spor #{dispute.id.slice(0, 8)}</div>
                  <h1 className="font-display text-2xl font-bold text-slate-900">
                    {CATEGORY_LABEL[dispute.category] || dispute.category}
                  </h1>
                  <div className="text-sm text-slate-500 mt-1">
                    Otevřeno {formatDate(dispute.created_at)} · <Link to={`/order/${dispute.order_id}`} className="text-sky-600 hover:underline">Objednávka</Link>
                  </div>
                </div>
                <Badge variant="secondary" className={meta.color} data-testid="dispute-status">{meta.label}</Badge>
              </div>

              {dispute.photos?.length > 0 && (
                <div className="mt-5">
                  <div className="tiny-label mb-2">Fotodokumentace</div>
                  <div className="grid grid-cols-3 gap-2">
                    {dispute.photos.map((p, i) => (
                      <img key={`${i}-${p.slice(-12)}`} src={p} alt={`foto ${i + 1}`} className="w-full h-28 object-cover rounded-lg border" />
                    ))}
                  </div>
                </div>
              )}

              {resolved && (
                <div className="mt-5 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-sm font-semibold text-emerald-900">Spor uzavřen: {meta.label}</div>
                  {dispute.refund_amount > 0 && (
                    <div className="text-sm text-emerald-700 mt-1">Náhrada zákazníkovi: {formatCzk(dispute.refund_amount)}</div>
                  )}
                  {dispute.admin_note && (
                    <div className="text-xs text-slate-600 mt-2">Poznámka: {dispute.admin_note}</div>
                  )}
                </div>
              )}
            </Card>

            <Card className="p-7">
              <div className="tiny-label mb-3 flex items-center gap-2">
                <MessageSquare className="w-3 h-3" /> Konverzace
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {(dispute.messages || []).map((m, i) => {
                  const mine = m.from_user_id === user?.id;
                  return (
                    <div key={`${m.at}-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-sky-600 text-white" : m.from_role === "admin" ? "bg-violet-100 text-violet-900" : "bg-slate-100 text-slate-800"}`}>
                        <div className={`text-[10px] mb-0.5 ${mine ? "text-sky-100" : "text-slate-500"}`}>
                          {m.from_name} · {m.from_role === "admin" ? "Admin" : m.from_role === "provider" ? "Prádelna" : "Zákazník"} · {formatDate(m.at)}
                        </div>
                        <div className="whitespace-pre-wrap">{m.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!resolved && (
                <form onSubmit={send} className="mt-4 flex gap-2">
                  <Textarea
                    rows={2}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Napište zprávu druhé straně…"
                    data-testid="dispute-message-input"
                  />
                  <Button type="submit" disabled={sending || !message.trim()} data-testid="dispute-message-send">
                    Odeslat
                  </Button>
                </form>
              )}
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-6">
              <div className="tiny-label mb-3">Účastníci</div>
              <div className="text-sm">
                <div className="text-slate-500">Zákazník</div>
                <div className="font-semibold text-slate-900">{dispute.customer_name}</div>
              </div>
              <div className="text-sm mt-3">
                <div className="text-slate-500">Prádelna</div>
                <div className="font-semibold text-slate-900">{dispute.provider_name || "—"}</div>
              </div>
            </Card>

            {user?.role === "admin" && !resolved && (
              <Card className="p-6 bg-violet-50 border-violet-200">
                <div className="tiny-label mb-3 text-violet-700 flex items-center gap-2">
                  <Gavel className="w-3 h-3" /> Rozhodnutí
                </div>
                <div className="space-y-3">
                  <div>
                    <Label>Akce</Label>
                    <Select value={action} onValueChange={setAction}>
                      <SelectTrigger data-testid="dispute-action"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_refund">Plná náhrada</SelectItem>
                        <SelectItem value="partial_refund">Částečná náhrada</SelectItem>
                        <SelectItem value="no_refund">Bez náhrady</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {action === "partial_refund" && (
                    <div>
                      <Label>Částka (Kč)</Label>
                      <Input
                        type="number" min="1" max={order?.total_price || 999999}
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        data-testid="dispute-refund-amount"
                      />
                      {order && <div className="text-xs text-slate-500 mt-1">Max: {formatCzk(order.total_price)}</div>}
                    </div>
                  )}
                  <div>
                    <Label>Poznámka</Label>
                    <Textarea rows={3} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Vysvětlení rozhodnutí…" data-testid="dispute-admin-note" />
                  </div>
                  <Button onClick={resolve} disabled={resolving} className="w-full" data-testid="dispute-resolve">
                    <Wallet className="w-4 h-4 mr-2" /> {resolving ? "Ukládám…" : "Uzavřít spor"}
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DisputeDetail;
